/**
 * SubscribeSearchButton
 * =============================================================================
 * Subscribe button for a keyword search query (US label changes only).
 *
 * - Mounted in KeywordSearchResults, with `kwRequest` passed down from SearchPage.
 * - Disabled when `kwRequest` is null (no active search).
 * - Calls POST /subscriptions {type:'search', keyword_request: kwRequest}.
 * - The US-only scope is stated in the button tooltip.
 * - Handles 409 ("Already subscribed") with a toast.
 * - Only shown when user is authenticated.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/authApi';
import type { KeywordSearchRequest } from '../types';

interface SubscribeSearchButtonProps {
  kwRequest: KeywordSearchRequest | null;
}

const SubscribeSearchButton: React.FC<SubscribeSearchButtonProps> = ({ kwRequest }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Reset the subscribed (checkmark) state whenever the underlying query
  // changes, so the confirmation doesn't carry over to a different search.
  // Keyed on the serialized request to be robust to prop-identity churn.
  const kwKey = kwRequest ? JSON.stringify(kwRequest) : null;
  useEffect(() => {
    setSubscribed(false);
  }, [kwKey]);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleSubscribe = useCallback(async () => {
    if (!user || !kwRequest || loading || subscribed) return;
    setLoading(true);
    try {
      await authApi.createSubscription({
        type: 'search',
        keyword_request: kwRequest as Record<string, unknown>,
      });
      setSubscribed(true);
      showToast("Subscribed. You'll be notified when new or updated labels match this search.", 'success');
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { detail?: unknown } } }).response;
      const status = resp?.status;
      if (status === 409) {
        // Already subscribed server-side — reflect that in the UI too.
        setSubscribed(true);
        showToast('Already subscribed to this search.', 'info');
      } else if (status === 400) {
        const detail = resp?.data?.detail;
        showToast(
          typeof detail === 'string' && detail ? detail : "This search can't be saved for alerts.",
          'error',
        );
      } else {
        showToast('Something went wrong. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [user, kwRequest, loading, subscribed, showToast]);

  if (!user) return null;

  return (
    // ``items-center`` keeps this button vertically aligned with the sibling
    // Compare / Group-by / Export buttons in the results header.
    <div className="relative inline-flex items-center">
      <button
        onClick={handleSubscribe}
        disabled={!kwRequest || loading}
        title={
          subscribed
            ? 'You are subscribed to this search'
            : !kwRequest
              ? 'Run a keyword search first to subscribe'
              : 'Saves this search and alerts you when matching labels change. Changes are detected when each source is refreshed.'
        }
        className={`btn-secondary text-[13px] disabled:cursor-not-allowed ${subscribed ? 'cursor-default' : ''}`}
      >
        {loading ? (
          <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : subscribed ? (
          // Confirmation checkmark once subscribed.
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ) : (
          // Yellow bell in the default (not-yet-subscribed) state.
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
        )}
        {subscribed ? 'Saved' : 'Save search'}
      </button>

      {/* Toast notification */}
      {toast && (
        <div
          className={`absolute top-full right-0 mt-2 z-50 px-3 py-2 text-xs rounded-lg shadow-lg whitespace-nowrap ${
            toast.type === 'success'
              ? 'bg-emerald-600 text-white'
              : toast.type === 'info'
              ? 'bg-indigo-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default SubscribeSearchButton;
